import React, { ChangeEvent, ReactElement, useEffect, useState } from "react";
import styles from "./SearchBar.module.scss";
import { useLocation, useNavigate } from "react-router-dom";
import { queryOptions, useQueryClient } from "@tanstack/react-query";
import { QueryKey } from "../../models/enums";
import { AddressPair } from "../../models/AddressPair";
import { IndexerService } from "../../services/indexer.service";
import { CloseIcon } from "@mantine/core";
import Button from "../Button/Button.component";
import { ExportImportTransaction } from "../../models/Transaction";
import { TransactionResponse } from "../../models/Response";
import { normalizeSearchInput, resolveSearchRoute } from "./searchBar.utils";

const searchQueryDefaults = {
  staleTime: Infinity,
  gcTime: Infinity,
  retry: false,
} as const;

// The keys match the ones the detail pages use, so a successful lookup
// is already cached when the page we navigate to mounts.
const addressPairQuery = (term: string) =>
  queryOptions({
    queryKey: [QueryKey.ethAddress, term],
    queryFn: async (): Promise<AddressPair | null> =>
      (await IndexerService.getAddressPair(term)) || null,
    ...searchQueryDefaults,
  });

const cChainTransactionQuery = (term: string) =>
  queryOptions({
    queryKey: [QueryKey.importExportTransactionsList, term],
    queryFn: async (): Promise<ExportImportTransaction | null> =>
      (await IndexerService.getCChainTransaction(term)) || null,
    ...searchQueryDefaults,
  });

const transactionQuery = (term: string) =>
  queryOptions({
    queryKey: [QueryKey.transaction, term],
    queryFn: (): Promise<TransactionResponse> =>
      IndexerService.getTransaction(term),
    ...searchQueryDefaults,
  });

export default function SearchBar(): ReactElement | null {
  const [input, setInput] = useState<string>("");
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const location = useLocation();
  const { pathname } = location;
  const [urlPath, setUrlPath] = useState<string>("");
  const [cChainAddress, setCChainAddress] = useState<string | null>(null);

  useEffect(() => {
    if (!pathname.includes("/address/")) {
      setCChainAddress(null);
    }
    const pathnameParts = pathname.split("/");
    const address = pathnameParts[pathnameParts.length - 1];
    setUrlPath(address);
  }, [pathname]);

  const handleInputChange = (event: ChangeEvent<HTMLInputElement>) => {
    setInput(event.target.value);
  };

  function isCurrentSearch(): boolean {
    return input === "" || input == cChainAddress || input == urlPath;
  }

  const search = async () => {
    if (isCurrentSearch()) {
      return;
    }
    // Look up the normalized value directly; never read it back from state.
    const term = normalizeSearchInput(input);
    setInput(term);

    if (term.startsWith("NodeID-")) {
      navigate(`validator/${term}`);
      return;
    }

    const [addressPair, cChainTransaction] = await Promise.all([
      queryClient.fetchQuery(addressPairQuery(term)).catch(() => null),
      queryClient.fetchQuery(cChainTransactionQuery(term)).catch(() => null),
      // Not used to pick the route; warms the cache for the transaction page.
      queryClient.prefetchQuery(transactionQuery(term)),
    ]);

    const route = resolveSearchRoute(term, addressPair, cChainTransaction);
    setCChainAddress(route.cChainAddress);
    navigate(route.path, { replace: route.replace });
  };

  const onFormSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    search();
  };

  const clear = () => {
    setInput("");
  };

  return (
    <>
      <div className={styles.searchBarWrapper}>
        <div className={styles.inputWrapper}>
          <div className={styles.inputHolder}>
            <form onSubmit={onFormSubmit}>
              <input
                className={`${input.length === 0 ? styles.empty : ""}`}
                value={input}
                placeholder={"Address, Transaction ID, Node ID..."}
                onChange={handleInputChange}
              />
              {input !== "" && (
                <div className={styles.clearButton} onClick={clear}>
                  <CloseIcon size={"25px"} />
                </div>
              )}
            </form>
          </div>
        </div>
        <div className={styles.buttonWrapper}>
          <Button
            addStyles={["full", isCurrentSearch() ? "disabled" : ""]}
            onClick={search}
          >
            Search
          </Button>
        </div>
      </div>
    </>
  );
}
